<template>
  <div>
    <div class="mb-4">
      <h4 class="font-weight-bold mb-2">
        <span class="text-muted font-weight-light">Autofixes <span class="material-icons md-14 arrow-east">east</span></span> Templates
      </h4>
    </div>
    <router-link class="btn btn-danger" :to="{ name: 'af-templates-add' }" v-if="$root.hasRole('admin.internal')">
      <i class="material-icons icon-create">control_point</i> Add template
    </router-link>
    <div class="mt-3" v-for="section in templateSections.filter(section => section.templates.length)" :key="section.name">
      <h4 class="card-title line-after p-0 pt-3 pb-3">{{ section.name }} templates</h4>
      <div class="card-deck card-deck-500">
        <div class="card mb-4" v-for="template in section.templates" :key="template.id" :style="{ 'opacity': template.client_hash !== $root.client.client_hash && template.status !== 'ACTIVE' ? 0.6 : 1 }">
          <div class="card-body py-0">
            <div class="d-flex mt-4 justify-content-between">
              <h5 class="card-title">
                <router-link v-if="canEdit" :to="{ name: 'af-templates-edit', params: { id: template.id } }" class="text-body">
                  {{ template.name }}
                </router-link>
                <template v-else>
                  {{ template.name }}
                </template>
              </h5>
              <div style="text-align: right;">
                <router-link v-if="template.client_hash !== $root.client.client_hash" class="btn btn-iso-create"
                             :to="{ name: 'af-templates-add', query: { copy: template.id } }">
                  copy &amp; configure
                </router-link><br />
                <template v-if="template.client_hash !== $root.client.client_hash && $root.hasRole('admin.internal') && template.status !== 'ACTIVE'">
                  <span class="badge" :class="statusColors[template.status]" >{{ template.status }}</span>
                </template>
              </div>
              <div v-if="template.client_hash === $root.client.client_hash">
                <span class="badge" :class="statusColors[template.status]" >{{ template.status }}</span>
              </div>
            </div>
            <img v-if="template.screenshots[0] && template.screenshots[0].screenshot_uri" @click="showScreenshot = template.screenshots[0]; showScreenshotModal = true;" :src="template.screenshots[0].screenshot_uri" style="max-width: 452px;" :title="template.name" :alt="template.name" class="img-fluid cursor-pointer" />
            <b-modal
                v-model="showScreenshotModal"
                @close="showScreenshotModal = false; showScreenshot = null;"
                v-if="showScreenshot"
                size="xl"
                hide-footer
                centered
            >
              <img
                  :src="showScreenshot.screenshot_uri"
                  class="img-fluid"
                  alt=""/>
            </b-modal>
            <div v-html="template.description" class="mt-3"></div>
          </div>
          <div class="card-footer">
            <div class="d-flex">
              <div class="flex-fill">
                <span class="badge badge-outline-secondary bg-light mr-1" v-for="category in template.categories"
                      :key="category.id">{{ category.name }}</span>
              </div>
              <div class="align-self-end text-nowrap text-big" v-if="template.client_hash === $root.client.client_hash || $root.hasRole('admin.internal')">
                <router-link v-if="canEdit" :to="{ name: 'af-templates-edit', params: { id: template.id } }" class="text-body" :class="{ 'adminOnly': template.client_hash !== $root.client.client_hash }">
                  <i class="cursor-pointer ion ion-md-create"></i>
                </router-link>
                <i class="cursor-pointer ion ion-md-trash ml-2" :class="{ 'adminOnly': template.client_hash !== $root.client.client_hash }" @click.stop="remove(template)" v-if="canDelete"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AutofixTemplates',
  metaInfo: {
    title: 'Autofixes - templates'
  },
  data () {
    return {
      categories: [],
      templates: [],
      statusColors: {
        ACTIVE: 'badge-success',
        ARCHIVED: 'badge-dark',
        INACTIVE: 'badge-dark',
        NODATA: 'badge-danger'
      },
      showScreenshot: null,
      showScreenshotModal: false
    }
  },
  computed: {
    canDelete () {
      return this.$root.hasRole('admin.internal') ||
        this.$root.hasRole('admin.company') ||
        this.$root.hasRole('configuration.delete')
    },
    canEdit () {
      return this.$root.hasRole('admin.internal') ||
        this.$root.hasRole('admin.company') ||
        this.$root.hasRole('configuration.modify')
    },
    templateSections () {
      const sections = []
      const usedTemplateIds = new Set()

      if (this.$root.client) {
        const ownTemplates = this.templates.filter(template => template.client_hash === this.$root.client.client_hash)
        ownTemplates.forEach(t => usedTemplateIds.add(t.id))
        sections.push({
          name: this.$root.client.name,
          templates: ownTemplates
        })
      }

      for (const category of this.categories) {
        const categoryTemplates = this.templates.filter(template => !template.client_hash && (template.categories || []).some(c => c.id === category.id))
        categoryTemplates.forEach(t => usedTemplateIds.add(t.id))
        sections.push({
          name: category.name,
          templates: categoryTemplates
        })
      }

      const sharedTemplates = this.templates.filter(template => !usedTemplateIds.has(template.id))
      sections.push({
        name: 'Shared',
        templates: sharedTemplates
      })

      return sections
    }
  },
  methods: {
    async reload () {
      if (!this.$root.client) return

      this.templates = (await this.$api.get('/interaction-templates/autofixes', this.$root.client.client_hash)).data.map((template) => {
        return {
          ...template,
          categories: template.categories || []
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name))
    },
    async remove (template) {
      if (!await this.$bvModal.msgBoxConfirm(`Do you want to delete template "${template.name}"?`, {
        title: 'Delete template?',
        okVariant: 'danger',
        cancelVariant: 'iso-link'
      })) {
        return
      }

      await this.$api.delete(`/interaction-templates/${template.id}`, this.$root.client.client_hash)
      await this.reload()
    }

  },
  watch: {
    '$root.client': {
      async handler () {
        this.categories = this.$root.client
          ? (await this.$api.get('/interaction-categories', this.$root.client.client_hash)).data
          : []
        await this.reload()
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
.card-deck {
  align-items: flex-start;
}
.card-deck-500 .card {
  flex: 0 0 500px;
}
</style>
